import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Users, LayoutGrid, ShieldCheck, Loader2, Mail, MailOpen, Trash2, Search } from "lucide-react";
import { cn } from "@/lib/utils";

const PLAN_STYLE = {
  free: "bg-muted text-muted-foreground",
  pro: "bg-primary/12 text-primary",
  elite: "bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))]",
};

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border bg-card p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="mt-1.5 font-display text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState(null);
  const [messages, setMessages] = useState(null);
  const [selected, setSelected] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null); // single user object, or "bulk"
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("all");

  const load = useCallback(async () => {
    const [s, u, m] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/messages"),
    ]);
    setStats(s.data);
    setUsers(u.data);
    setMessages(m.data);
  }, []);

  useEffect(() => {
    if (user?.role === "admin") load().catch(() => toast.error("Couldn't load admin data"));
  }, [user, load]);

  if (user && user.role !== "admin") return <Navigate to="/app" replace />;

  const setPlan = async (u, plan) => {
    try {
      await api.put(`/admin/users/${u.user_id}/plan`, { plan });
      toast.success(`${u.email} moved to ${plan.charAt(0).toUpperCase() + plan.slice(1)}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't change plan");
    }
  };

  const filteredUsers = (users || []).filter((u) => {
    if (planFilter !== "all" && u.plan !== planFilter) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      if (!`${u.name} ${u.email}`.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const deletableUsers = filteredUsers.filter((u) => u.user_id !== user?.user_id);
  const allSelected = deletableUsers.length > 0 && deletableUsers.every((u) => selected.includes(u.user_id));

  const toggleSelectAll = () => {
    const visibleIds = deletableUsers.map((u) => u.user_id);
    setSelected((prev) => (allSelected ? prev.filter((id) => !visibleIds.includes(id)) : [...new Set([...prev, ...visibleIds])]));
  };

  const toggleSelect = (uid) => {
    setSelected((prev) => (prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]));
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      if (deleteTarget === "bulk") {
        await api.post("/admin/users/bulk-delete", { user_ids: selected });
        toast.success(`${selected.length} customer${selected.length === 1 ? "" : "s"} deleted`);
        setSelected([]);
      } else {
        await api.delete(`/admin/users/${deleteTarget.user_id}`);
        toast.success(`${deleteTarget.email} deleted`);
        setSelected((prev) => prev.filter((id) => id !== deleteTarget.user_id));
      }
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete customer");
    } finally {
      setDeleting(false);
    }
  };

  const markRead = async (id) => {
    await api.put(`/admin/messages/${id}/read`);
    load();
  };

  const unread = stats?.unread_messages || 0;

  return (
    <AppShell>
      <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:py-8" data-testid="admin-page">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <h1 className="font-display text-2xl font-semibold tracking-tight md:text-3xl">Admin</h1>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">Customers, plans and inbound messages. One MetaApi platform key powers all live syncing.</p>

        <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {stats ? (
            <>
              <StatCard icon={Users} label="Customers" value={stats.total_users} />
              <StatCard icon={LayoutGrid} label="Connected accounts" value={stats.connected_accounts} />
            </>
          ) : (
            [...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)
          )}
        </div>

        <Tabs defaultValue="customers" className="mt-8">
          <TabsList>
            <TabsTrigger value="customers" data-testid="admin-tab-customers">Customers</TabsTrigger>
            <TabsTrigger value="messages" className="gap-1.5" data-testid="admin-tab-messages">
              Messages
              {unread > 0 && <Badge className="h-4 min-w-4 px-1 text-[9px]" data-testid="unread-messages-badge">{unread}</Badge>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="customers" className="mt-4">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <div className="relative w-full max-w-xs">
                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or email…" className="h-8 pl-8 text-xs"
                  data-testid="admin-customer-search" />
              </div>
              <Select value={planFilter} onValueChange={setPlanFilter}>
                <SelectTrigger className="h-8 w-32 text-xs" data-testid="admin-plan-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All plans</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="elite">Elite</SelectItem>
                </SelectContent>
              </Select>
              {(search || planFilter !== "all") && (
                <span className="text-xs text-muted-foreground" data-testid="admin-customer-filter-count">
                  {filteredUsers.length} of {(users || []).length}
                </span>
              )}
            </div>
            {selected.length > 0 && (
              <div className="mb-3 flex items-center justify-between rounded-xl border bg-muted/40 px-4 py-2" data-testid="bulk-actions-bar">
                <span className="text-sm font-medium">{selected.length} selected</span>
                <Button variant="destructive" size="sm" className="h-7 gap-1.5 text-xs"
                  onClick={() => setDeleteTarget("bulk")} data-testid="bulk-delete-button">
                  <Trash2 className="h-3.5 w-3.5" /> Delete selected
                </Button>
              </div>
            )}
            <div className="overflow-x-auto rounded-2xl border">
              <table className="w-full min-w-[720px] text-sm" data-testid="admin-users-table">
                <thead className="bg-surface-2">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-2.5 font-medium">
                      <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll}
                        disabled={!deletableUsers.length} data-testid="select-all-customers" />
                    </th>
                    <th className="px-4 py-2.5 font-medium">Customer</th>
                    <th className="px-4 py-2.5 font-medium">Plan</th>
                    <th className="px-4 py-2.5 text-right font-medium">Workspaces</th>
                    <th className="px-4 py-2.5 text-right font-medium">Accounts</th>
                    <th className="px-4 py-2.5 text-right font-medium">Joined</th>
                    <th className="px-4 py-2.5 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users === null && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
                  )}
                  {users !== null && !filteredUsers.length && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground" data-testid="admin-customer-no-results">
                      No customers match your search.
                    </td></tr>
                  )}
                  {filteredUsers.map((u) => (
                    <tr key={u.user_id} className="border-t" data-testid={`admin-user-row-${u.user_id}`}>
                      <td className="px-4 py-2.5">
                        {u.user_id !== user?.user_id && (
                          <Checkbox checked={selected.includes(u.user_id)} onCheckedChange={() => toggleSelect(u.user_id)}
                            data-testid={`select-customer-${u.user_id}`} />
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="font-medium">{u.name}</div>
                            <div className="text-xs text-muted-foreground">{u.email}</div>
                          </div>
                          {u.role === "admin" && <Badge variant="secondary" className="text-[9px]">Admin</Badge>}
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <Select value={u.plan} onValueChange={(v) => setPlan(u, v)}>
                          <SelectTrigger className={cn("h-7 w-24 border-transparent text-xs font-medium", PLAN_STYLE[u.plan])}
                            data-testid={`plan-select-${u.user_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="free">Free</SelectItem>
                            <SelectItem value="pro">Pro</SelectItem>
                            <SelectItem value="elite">Elite</SelectItem>
                          </SelectContent>
                        </Select>
                        {u.plan_source === "stripe" && <span className="ml-1.5 text-[10px] text-muted-foreground">via Stripe</span>}
                        {u.plan_source === "cancelled" && <span className="ml-1.5 text-[10px] text-muted-foreground">cancelled</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{u.workspaces}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{u.accounts}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs text-muted-foreground">{fmtDate(u.created_at)}</td>
                      <td className="px-4 py-2.5 text-right">
                        {u.user_id !== user?.user_id && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive"
                            onClick={() => setDeleteTarget(u)} data-testid={`delete-customer-${u.user_id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="messages" className="mt-4">
            <div className="space-y-3" data-testid="admin-messages-list">
              {messages !== null && !messages.length && (
                <div className="rounded-2xl border px-4 py-10 text-center text-sm text-muted-foreground">
                  <Mail className="mx-auto mb-2 h-6 w-6 opacity-50" />
                  No messages yet. Contact form submissions land here.
                </div>
              )}
              {(messages || []).map((m) => (
                <div key={m.message_id}
                  className={cn("rounded-2xl border bg-card p-4", !m.read && "ring-1 ring-ring/30")}
                  data-testid={`admin-message-${m.message_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {!m.read ? <Mail className="h-4 w-4 text-primary" /> : <MailOpen className="h-4 w-4 text-muted-foreground" />}
                      <span className="text-sm font-medium">{m.name}</span>
                      <span className="text-xs text-muted-foreground">{m.email}</span>
                      {!m.read && <Badge className="text-[9px]">New</Badge>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[10px] text-muted-foreground">{fmtDateTime(m.created_at)}</span>
                      {!m.read && (
                        <Button variant="outline" size="sm" className="h-6 px-2 text-[11px]" onClick={() => markRead(m.message_id)}
                          data-testid={`mark-read-${m.message_id}`}>
                          Mark read
                        </Button>
                      )}
                    </div>
                  </div>
                  {m.subject && <div className="mt-2 text-sm font-medium">{m.subject}</div>}
                  <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">{m.message}</p>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteTarget === "bulk" ? `Delete ${selected.length} customers?` : `Delete ${deleteTarget?.email}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes {deleteTarget === "bulk" ? "these accounts" : "this account"}, along with
              their workspaces, connected trading accounts, trades and journal entries. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-customer-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDelete} disabled={deleting} data-testid="delete-customer-confirm">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
